/**
 * Date formatting for blog posts.
 *
 * Everything here pins the timezone to UTC on purpose. Blog pages are
 * prerendered in Node on a build machine and then hydrated in a visitor's
 * browser; if the two format the same timestamp in their own local zones, a
 * post published at 09:00 UTC renders as "23 August" on the server and
 * "22 August" for a reader in Los Angeles. Vue then reports a hydration
 * mismatch and patches the text after paint, which is both a layout shift and a
 * date that visibly changes as the page loads.
 *
 * Posts carry a day, not a moment — `date: 2026-08-23` in the frontmatter — so
 * UTC is not an approximation here. It is the zone the author actually wrote.
 */

const LONG = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
})

const SHORT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
})

/** "23 August 2026". Empty string for a missing or unparseable timestamp. */
export function formatPostDate(iso: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? '' : LONG.format(date)
}

/** "23 Aug 2026" — the listing, where the date sits on one line with the rest. */
export function formatPostDateShort(iso: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? '' : SHORT.format(date)
}

/**
 * The `datetime` attribute of a `<time>` element: a bare YYYY-MM-DD.
 *
 * This is the machine-readable half of every date on the page — what a crawler
 * reads to date the article, regardless of how the visible text is worded.
 */
export function toDateAttr(iso: string): string {
  return iso ? iso.slice(0, 10) : ''
}
